import { money } from 'utilities/helper/format'
import { SectionError } from 'components/states'
import DotsLoading from 'components/loadings/dotsLoading'
import { useCashboxTransactions } from '../hooks'

interface Props {
    id?: string
    search: string
}

/** Transaction.state — `utils/constants/choice.py` */
const STATE_LABELS: Record<string, string> = {
    suc: 'موفق',
    inp: 'در حال انجام',
    tbn: 'در حال انتقال به بانک',
    ini: 'ایجاد شده',
    fld: 'ناموفق',
}

/**
 * `list_transactions` returns cash-INs and cash-OUTs of the current period
 * together (`generate_transactions_list(..., is_open=True)`), and the only
 * thing distinguishing them is source/destination. `mba`/`gtw`/`wlt` sources
 * are money coming in; a `member_bankaccount` destination is a loan going out.
 */
const isOutgoing = (t: ICashboxTransaction) => t.destination === 'mba'

export default function TransactionsTab({ id, search }: Props) {
    const { data, isLoading, isError, refetch } = useCashboxTransactions(
        id,
        search
    )

    if (isLoading) return <DotsLoading />
    if (isError)
        return (
            <div className="p-4">
                <SectionError onRetry={() => refetch()} />
            </div>
        )

    const items = data?.results ?? []

    if (items.length === 0)
        return (
            <p className="text-sm text-text-secondary text-center py-10">
                تراکنشی ثبت نشده است
            </p>
        )

    return (
        <div className="px-4 py-3">
            <div className="rounded-card border border-divider bg-white overflow-hidden">
                {items.map((t, index) => {
                    const outgoing = isOutgoing(t)
                    return (
                        <div
                            key={t.pk ?? t.id ?? index}
                            className="flex items-center justify-between px-3 py-3 border-b border-divider last:border-b-0"
                        >
                            <div className="min-w-0">
                                <p className="text-sm text-black truncate">
                                    {outgoing
                                        ? t.receiver_name?.trim() ||
                                          'پرداخت وام'
                                        : t.payer_name?.trim() ||
                                          t.receiver_name?.trim() ||
                                          'واریز سهم'}
                                </p>
                                <p className="text-xs text-text-secondary mt-0.5">
                                    {outgoing ? 'دریافت وام' : 'واریز سهم'}
                                    {t.state && STATE_LABELS[t.state]
                                        ? ` · ${STATE_LABELS[t.state]}`
                                        : ''}
                                </p>
                            </div>
                            <p
                                className={`text-sm font-bold shrink-0 pl-1 ${
                                    outgoing
                                        ? 'text-[#E0564C]'
                                        : 'text-[#4CA632]'
                                }`}
                            >
                                {/* The sign is isolated with the digits, not
                                    left loose in the RTL run. `+` and `−` are
                                    bidi-neutral, so on their own they resolve
                                    to the paragraph direction and get painted
                                    at the far RIGHT of «۱۲٬۰۰۰٬۰۰۰ تومان» —
                                    nowhere near the number they modify. Same
                                    fix, same reason, as the member record in
                                    `memberRecord/components/Blocks.tsx`. */}
                                <span
                                    dir="ltr"
                                    style={{ unicodeBidi: 'isolate' }}
                                >
                                    {outgoing ? '−' : '+'}
                                    {money(t.amount)}
                                </span>{' '}
                                تومان
                            </p>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
